import React, { useState } from 'react';
import { Button, Card, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import posthog, {
  POSTHOG_GROUP_TYPES,
  setPosthogGroups,
} from 'src/services/posthog';
import {
  captureExamEvent,
  EXAM_EVENTS,
  markExamStarted,
} from 'src/services/posthogEvents';
import { APP_EVENTS, captureAppEvent } from 'src/services/posthogEvents';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import {
  CustomModal,
  CustomModalBody,
  CustomModalFooter,
  CustomModalHeader,
  CustomModalTitle,
} from 'src/components/custom_components/CustomModal';

import { I18NextNS } from 'src/models/Language.model';
import { Session } from 'src/models/Session.model';

import useDateFormats from 'src/hooks/useDateFormats';
import useNavigateAfterConfirm from 'src/hooks/useNavigateAfterConfirm';
import useResumeExam from 'src/hooks/useResumeExam';

import PatientInfoRow from './PatientInfoRow';

type TeleophthPatientConfirmationModalProps = {
  formValues: Partial<Session>;
  isShowConfirmModal: boolean;
  handleCloseConfirmModal: () => void;
};

export default function TeleophthPatientConfirmationModal({
  formValues,
  isShowConfirmModal,
  handleCloseConfirmModal,
}: TeleophthPatientConfirmationModalProps) {
  const { t } = useTranslation(I18NextNS.COMMON);
  const { formatDate } = useDateFormats();
  const { updateSession } = useRemoteBoundStore();
  const { initializeResumedExam } = useResumeExam();
  const navigateAfterConfirm = useNavigateAfterConfirm();
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    updateSession(formValues);

    setIsConfirming(true);
    const resolvedSiteId = await initializeResumedExam(
      formValues.orderId!,
      formValues.siteId,
    );

    posthog.group(POSTHOG_GROUP_TYPES.ORDER, formValues.orderId!);
    if (resolvedSiteId) {
      setPosthogGroups({ [POSTHOG_GROUP_TYPES.SITE]: resolvedSiteId });
    }
    markExamStarted();
    captureExamEvent(EXAM_EVENTS.EXAM_RESUMED, {
      order_id: formValues.orderId!,
    });
    setIsConfirming(false);
    captureAppEvent(APP_EVENTS.PATIENT_INFORMATION_CONFIRMED);
    navigateAfterConfirm();
  };

  return (
    <CustomModal
      closeButton
      show={isShowConfirmModal}
      onHide={handleCloseConfirmModal}
      scrollable={true}
      fullscreen="xl-down"
    >
      <CustomModalHeader>
        <CustomModalTitle data-test="confirm-patient-info-modal-title">
          {t('patientFields.confirmPatientInformation')}
        </CustomModalTitle>
      </CustomModalHeader>
      <CustomModalBody>
        <Card>
          <Card.Body>
            <Stack gap={2}>
              <PatientInfoRow
                label={t('patientFields.PID')}
                value={formValues.id}
                dataTest="patient-info-id"
              />
              <PatientInfoRow
                label={t('patientFields.First Name')}
                value={formValues.firstName}
                dataTest="patient-info-first-name"
              />
              <PatientInfoRow
                label={t('patientFields.Last Name')}
                value={formValues.lastName}
                dataTest="patient-info-last-name"
              />
              <PatientInfoRow
                label={t('patientFields.DOB')}
                value={formatDate(formValues.dob)}
                dataTest="patient-info-dob"
              />
            </Stack>
          </Card.Body>
        </Card>
      </CustomModalBody>

      <CustomModalFooter>
        <Button
          variant="primary-100"
          onClick={handleCloseConfirmModal}
          disabled={isConfirming}
          data-track="exam.patient-confirmation-modal.cancel"
          data-test="confirm-patient-cancel"
          data-observe
        >
          {t('modal.cancel')}
        </Button>

        <Button
          variant="primary"
          className="shadow"
          onClick={handleConfirm}
          disabled={isConfirming}
          data-track="exam.patient-confirmation-modal.confirm"
          data-test="confirm-patient-information"
          data-observe
        >
          {isConfirming ? t('modal.confirming') : t('modal.confirm')}
        </Button>
      </CustomModalFooter>
    </CustomModal>
  );
}
